import React, {useState} from 'react';
import {NavDropdown, ToggleButton} from 'react-bootstrap';
import { InvoiceDto } from '../../../models/invoice.dto';
import 'react-datepicker/dist/react-datepicker.css';
import '.././invoices-list.scss';
import {
   useDownloadInvoiceMutation,
   useApproveInvoiceMutation, useDownloadInvoiceReceiptMutation,
} from '../../../store/invoices/invoices.api';
import { useTranslation } from 'react-i18next';
import RejectDialog from './reject-dialog/reject-dialog';
import HistoryDialog from '../history-dialog/history-dialog';
import {UserDto} from '../../../models/user.dto';
import PdfViewer from '../../elements/pdf-viewer/pdf-viewer';
import DeleteDialog from './delete-dialog/delete-dialog';
import {ChevronDown, ChevronUp, ThreeDotsVertical} from 'react-bootstrap-icons';
import './invoices-list-item.scss';
import { ColumnConfig } from '../columns/column-config';
import { IS_ADMIN } from '../../../utils/role';
import PaymentDateModal from '../../date-modal/payment-date-modal';
import { notBlank } from '../../../utils/base';
import Status from './status';
import Amount from './amount/amount';
import moment from 'moment';
import editIcon from '../../../assets/icons/bank-account-edit.svg';

interface PropsType {
   columns: ColumnConfig[],
   invoice: InvoiceDto;
   handleInvoiceSelection: (invoice: InvoiceDto, checked: boolean) => void;
   selected: InvoiceDto[];
   user?: UserDto,
   costCentersExist: boolean,
   isTutorialTarget?: boolean
}

const InvoicesListItem = ({
   columns,
   invoice,
   selected,
   handleInvoiceSelection,
   user,
   costCentersExist,
   isTutorialTarget = false
}: PropsType) => {
   const [showEditDateModal, setShowEditDateModal] = useState(false);
   const [showRejectModal, setShowRejectModal] = useState(false);
   const [showHistoryModal, setShowHistoryModal] =  useState(false);
   const [showDeleteDialog, setShowDeleteDialog] =  useState(false);
   const [showPdfViewer, setShowPdfViewer] =  useState(false);
   const [showMobileDetails, setShowMobileDetails] = useState(false);
   const [downloadInvoice] = useDownloadInvoiceMutation();
   const [downloadInvoiceReceipt] = useDownloadInvoiceReceiptMutation();
   const [approveInvoice] = useApproveInvoiceMutation();
   const [translate] = useTranslation(['account', 'common', 'invoice']);

   const receiptExists = notBlank(invoice.receiptPath);

   const isInvoiceSelected = () => {
      return selected.some((selectedInvoice) => selectedInvoice?.invoiceId === invoice?.invoiceId);
   };

   const onClickDownload = () => receiptExists
      ? downloadInvoiceReceipt({ invoiceId: invoice.invoiceId })
      : downloadInvoice({ invoiceId: invoice.invoiceId });

   const onClickApprove = () => approveInvoice({ invoiceId: invoice.invoiceId });

   const activeRejections =  invoice.rejections.filter(i => i.active);
   const approvedByUser = invoice.approvals.some((a) => a.active && a.email === user?.email);
   const rejectedByUser = activeRejections.some((r) => r.email == user?.email);

   const renderInvoiceActions = () => (
      <>
         <NavDropdown.Item onClick={() => setShowPdfViewer(true)}>
            {translate('invoice:invoice.viewInvoice')}
         </NavDropdown.Item>
         <NavDropdown.Item onClick={onClickDownload}>
            {translate('invoice:invoice.downloadInvoice')}
         </NavDropdown.Item>
         <NavDropdown.Item onClick={() => setShowHistoryModal(true)}>
            {translate('common:common.viewHistory')}
         </NavDropdown.Item>
         {IS_ADMIN() &&
            <>
               <NavDropdown.Divider/>
               <NavDropdown.Item onClick={() => setShowEditDateModal(true)}>
                  {translate('invoice:invoice.editPaymentDate')}
               </NavDropdown.Item>
               <NavDropdown.Divider/>
               <NavDropdown.Item onClick={onClickApprove} disabled={!!activeRejections?.length || approvedByUser}>
                  {translate('invoice:invoice.approve')}
               </NavDropdown.Item>
               <NavDropdown.Item onClick={() => setShowRejectModal(true)} disabled={rejectedByUser}>
                  {translate('invoice:invoice.reject')}
               </NavDropdown.Item>
               <NavDropdown.Divider />
               <NavDropdown.Item onClick={() => setShowDeleteDialog(true)} disabled={!activeRejections?.length}>
                  {translate('common:common.delete')}
               </NavDropdown.Item>
            </>
         }
      </>
   );

   return (
      <>
         <tr className='desktop-invoice-row' data-tour={isTutorialTarget ? 'invoice-details' : undefined}>
            <td className={'selection'} style={{ width: 30 }}>
               <ToggleButton
                  style={{ padding: 6 }}
                  id={`toggle-check-${invoice.invoiceId}`}
                  type='checkbox'
                  variant='outline-primary'
                  checked={isInvoiceSelected()}
                  value={invoice.invoiceId}
                  onChange={() => handleInvoiceSelection(invoice, !isInvoiceSelected())}
               ></ToggleButton>
            </td>
            {
               columns.map(c => {
                  if (c.show) {
                     return c.renderCell(invoice, () => setShowPdfViewer(true));
                  }
               })
            }
            <td className={'action'}>
               <NavDropdown title={<ThreeDotsVertical className='invoice-actions-icon' />} className='actions' >
                  {renderInvoiceActions()}
               </NavDropdown>
            </td>
         </tr>

         <tr className='mobile-invoice-row' data-tour={isTutorialTarget ? 'invoice-details-mobile' : undefined}>
            <td colSpan={columns.length + 2}>
               <article className='invoice-card'>
                  <div className='invoice-card-summary'>
                     <ToggleButton
                        id={`mobile-toggle-check-${invoice.invoiceId}`}
                        type='checkbox'
                        variant='outline-primary'
                        checked={isInvoiceSelected()}
                        value={invoice.invoiceId}
                        onChange={() => handleInvoiceSelection(invoice, !isInvoiceSelected())}
                        aria-label={`Select invoice ${invoice.invoiceId}`}
                     />
                     <div className='invoice-card-main'>
                        <span className='invoice-card-id'>{invoice.invoiceId}</span>
                        <strong>{invoice.company}</strong>
                        <span className='invoice-card-amount'><Amount invoice={invoice} compact /> <span>{invoice.currency}</span></span>
                     </div>
                     <div className='invoice-card-status'>
                        <NavDropdown title={<ThreeDotsVertical className='invoice-actions-icon' aria-label='Invoice actions' />} align='end'>
                           <NavDropdown.Item onClick={() => setShowPdfViewer(true)}>
                              <img className='invoice-edit-icon' src={editIcon} alt='' aria-hidden='true' />
                              {translate('invoice:invoice.editDetails', { defaultValue: 'Edit Details' })}
                           </NavDropdown.Item>
                           <NavDropdown.Divider />
                           {renderInvoiceActions()}
                        </NavDropdown>
                        <Status approvals={invoice.approvals} rejections={invoice.rejections} />
                     </div>
                  </div>
                  {showMobileDetails && (
                     <div className='invoice-card-details'>
                        <dl>
                           <div><dt>ID</dt><dd>{invoice.invoiceId}</dd></div>
                           <div><dt>{translate('invoice:invoice.company')}</dt><dd>{invoice.company}</dd></div>
                           <div><dt>{translate('invoice:invoice.bankAccount')}</dt><dd>{invoice.bankAccount?.name || '—'}</dd></div>
                           <div><dt>{translate('invoice:invoice.vatCode')}</dt><dd>{invoice.vat?.code || invoice.vatCode || '—'}</dd></div>
                           <div><dt>{translate('invoice:invoice.accountingNumber')}</dt><dd>{invoice.accountingNumber || '—'}</dd></div>
                           {costCentersExist && <div><dt>{translate('invoice:invoice.costCenter')}</dt><dd>{invoice.costCenter || '—'}</dd></div>}
                           <div><dt>{translate('invoice:invoice.accountingRemark')}</dt><dd>{invoice.accountingRemark || '—'}</dd></div>
                           <div><dt>{translate('invoice:invoice.description')}</dt><dd>{invoice.description || '—'}</dd></div>
                           <div><dt>{translate('invoice:invoice.generationDate')}</dt><dd>{invoice.dtaDate ? moment(invoice.dtaDate).format('DD-MM-YYYY') : '—'}</dd></div>
                           <div><dt>{translate('invoice:invoice.amountAndCurrency')}</dt><dd className='mobile-detail-amount'><Amount invoice={invoice} compact /> {invoice.currency}</dd></div>
                        </dl>
                     </div>
                  )}
                  <button type='button' className='invoice-card-toggle' onClick={() => setShowMobileDetails(open => !open)}>
                     {showMobileDetails ? 'View less' : 'View more'}
                     {showMobileDetails ? <ChevronUp /> : <ChevronDown />}
                  </button>
               </article>
            </td>
         </tr>

         <PaymentDateModal
            invoice={invoice}
            showEditDateModal={showEditDateModal}
            setShowEditDateModal={setShowEditDateModal}
         />

         <RejectDialog invoiceId={invoice.invoiceId}
                       show={showRejectModal}
                       onHide={() => setShowRejectModal(false)} />

         <DeleteDialog show={showDeleteDialog}
                       onHide={() => setShowDeleteDialog(false)}
                       invoiceId={invoice.invoiceId}
                       invoiceName={invoice.company} />

         {showHistoryModal &&
           <HistoryDialog show={showHistoryModal}
                          onHide={() => setShowHistoryModal(false)}
                          invoice={invoice}
            />
         }
         {showPdfViewer &&
            <PdfViewer
               invoice={invoice}
               onHide={() => setShowPdfViewer(false)}
               show={showPdfViewer}
               costCentersExist={costCentersExist}
               user={user}
            />
         }
      </>
   );
};

export default InvoicesListItem;
